import { parseLessonContent, type LessonContent } from "@/lib/lesson-content/schema";

/** Dev 1 sample GPU memory hierarchy lesson — validates against LessonContentSchema. */
const GPU_LESSON_RAW = {
  version: 1,
  format: "blocks",
  sections: [
    {
      id: "0a1b2c3d-0100-4a00-8000-000000000001",
      title: "GPU Memory Hierarchy",
      required_to_advance: false,
      blocks: [
        {
          id: "0a1b2c3d-0001-4a00-8000-000000000001",
          type: "rich_text",
          personalized: false,
          data: {
            markdown:
              "## GPU Memory Hierarchy\n\nGPUs keep data close to the cores.\n\n| Tier | Speed | Scope |\n|------|-------|-------|\n| Registers | Fastest | Per-thread |\n| HBM | Slower | Whole device |\n\n:::info Key idea\nCloser memory is faster — and scarcer.\n:::",
          },
        },
        {
          id: "0a1b2c3d-0002-4a00-8000-000000000002",
          type: "image",
          personalized: false,
          data: {
            image_url: "http://localhost:3000/ile-demo/gpu-hierarchy.svg",
            alt: "GPU memory tiers",
            caption: "Figure 1. Tiers from registers to global HBM.",
          },
        },
      ],
    },
    {
      id: "0a1b2c3d-0200-4a00-8000-000000000002",
      title: "Explore the architecture",
      required_to_advance: true,
      blocks: [
        {
          id: "0a1b2c3d-0003-4a00-8000-000000000003",
          type: "image_hotspot",
          personalized: false,
          data: {
            image_url: "http://localhost:3000/ile-demo/gpu-die.svg",
            alt: "GPU die layout",
            hotspots: [
              { id: "h1", x: 25, y: 30, title: "Register File", info: "On-core, per-thread, fastest." },
              { id: "h2", x: 70, y: 65, title: "HBM", info: "High-bandwidth global memory." },
            ],
          },
        },
        {
          id: "0a1b2c3d-0004-4a00-8000-000000000004",
          type: "flip_cards",
          personalized: false,
          data: {
            cards: [
              { id: "c1", front: "L1 Cache", back: "Fast on-core SRAM shared within a block." },
              { id: "c2", front: "Register", back: "Fastest unit; holds a thread's operands." },
            ],
          },
        },
        {
          id: "0a1b2c3d-0005-4a00-8000-000000000005",
          type: "accordion_tabs",
          personalized: false,
          data: {
            display: "accordion",
            sections: [
              { id: "s1", title: "User space", body: "Handles API calls and kernel launches." },
              { id: "s2", title: "Driver", body: "Schedules work onto the device." },
            ],
          },
        },
        {
          id: "0a1b2c3d-0006-4a00-8000-000000000006",
          type: "stepped_timeline",
          personalized: false,
          data: {
            steps: [
              { id: "p1", label: "Allocate", body: "Reserve device memory." },
              { id: "p2", label: "Copy", body: "Move inputs host to device." },
              { id: "p3", label: "Launch", body: "Run the kernel across threads." },
            ],
          },
        },
      ],
    },
    {
      id: "0a1b2c3d-0300-4a00-8000-000000000003",
      title: "Check your understanding",
      required_to_advance: true,
      blocks: [
        {
          id: "0a1b2c3d-0007-4a00-8000-000000000007",
          type: "mcq",
          personalized: false,
          data: {
            question: "Which memory is closest to the core?",
            options: ["HBM", "Register File", "SSD"],
            correct_index: 1,
            points: 10,
            explanation: "Registers are on-core and per-thread.",
          },
        },
        {
          id: "0a1b2c3d-0008-4a00-8000-000000000008",
          type: "categorization",
          personalized: false,
          data: {
            buckets: [
              { id: "b_fast", label: "On-core / fast" },
              { id: "b_global", label: "Global / large" },
            ],
            items: [
              { id: "i1", label: "Register File", correct_bucket_id: "b_fast" },
              { id: "i2", label: "HBM", correct_bucket_id: "b_global" },
            ],
            points_per_match: 5,
          },
        },
      ],
    },
    {
      id: "0a1b2c3d-0400-4a00-8000-000000000004",
      title: "Apply it",
      required_to_advance: true,
      blocks: [
        {
          id: "0a1b2c3d-0009-4a00-8000-000000000009",
          type: "sequencing",
          personalized: false,
          data: {
            items: [
              { id: "q_alloc", label: "Allocate device memory" },
              { id: "q_copy", label: "Copy inputs to device" },
              { id: "q_launch", label: "Launch kernel" },
            ],
            correct_order: ["q_alloc", "q_copy", "q_launch"],
            points: 15,
          },
        },
        {
          id: "0a1b2c3d-0010-4a00-8000-000000000010",
          type: "fill_blank",
          personalized: false,
          data: {
            template: "ML workloads run on {{unit}}, which write operands to {{store}}.",
            blanks: [
              { id: "unit", options: ["Tensor Cores", "Static Buffers"], correct_index: 0 },
              { id: "store", options: ["registers", "SATA drives"], correct_index: 0 },
            ],
            points_per_blank: 5,
          },
        },
      ],
    },
  ],
} as const;

export const GPU_LESSON_FIXTURE: LessonContent = parseLessonContent(GPU_LESSON_RAW);
